import React, { useState } from 'react';
import { Box, Button, useTheme, IconButton, Tooltip, CircularProgress } from '@mui/material';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  useGetTransactionCountStatusQuery,
  useExportTransactionStatsMutation,
} from '@/services/dashboardApi';
import excelIcon from '../../assets/excel-icon.svg';

interface TransactionChartProps {
  amcId?: string;
}

export const TransactionChart: React.FC<TransactionChartProps> = ({ amcId }) => {
  const [activePeriod, setActivePeriod] = useState<string>('1W');
  const theme = useTheme();
  const periods = [
    { label: '1W', value: '1W' },
    { label: '1M', value: '1M' },
    { label: '6M', value: '6M' },
    { label: '1Y', value: '1Y' },
    { label: 'All', value: 'All' },
  ];

  // Build API parameters with optional AMC filter
  const apiParams: any = {
    type: activePeriod as '1W' | '1M' | '6M' | '1Y' | 'All',
  };
  if (amcId) {
    apiParams.amcId = amcId;
  }

  const { data } = useGetTransactionCountStatusQuery(apiParams);
  const [exportTransactionStats, { isLoading: isDownloading }] =
    useExportTransactionStatsMutation();

  const handleDownload = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    try {
      const blob = await exportTransactionStats({
        amcId,
        type: activePeriod as '1W' | '1M' | '6M' | '1Y' | 'All',
      }).unwrap();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = 'transaction-stats.xlsx';

      document.body.appendChild(link);

      // Use requestAnimationFrame to ensure proper download
      requestAnimationFrame(() => {
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      });
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const handlePeriodChange = (period: string) => {
    setActivePeriod(period);
    // In a real application, you would fetch data for the selected period here
  };
  const TimePeriodButton: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({
    label,
    active,
    onClick,
  }) => (
    <Button
      onClick={onClick}
      sx={{
        bgcolor: active ? theme.palette.primary.light : theme.palette.background.paper,
        color: active ? theme.palette.primary.main : theme.typography.h1.color,
        border: active ? theme.borders.borderMedium : theme.borders.borderTime,
        borderRadius: '20px',
        minWidth: '60px',
        fontWeight: theme.typography.subtitle1.fontWeight,
        '&:hover': {
          bgcolor: active ? theme.palette.primary.light : theme.palette.primary.light,
        },
      }}
    >
      {label}
    </Button>
  );

  // Separate optional summary row and time-series rows
  const summaryTotal =
    Array.isArray(data) && data.length > 0 ? (data[0] as any)?.totalTransactions : undefined;
  // Filter out any non timeseries item (like the summary row)
  const timeSeries = Array.isArray(data)
    ? data.filter((d: any, idx: number) => idx !== 0 && d && (d.TransactionDate || d.MonthName))
    : [];

  const categories = timeSeries.map((item: any) => {
    if (activePeriod === '1Y' || activePeriod === '6M') {
      return item.MonthName || '';
    }
    const date = item.TransactionDate ? new Date(item.TransactionDate) : undefined;
    return date ? date.toLocaleDateString('en-US', { month: 'short', day: '2-digit' }) : '';
  });

  const transactionChartOptions: Highcharts.Options = {
    credits: {
      enabled: false,
    },
    chart: {
      type: 'area',
    },
    title: {
      text: 'Number of Transactions',
      align: 'left',
      style: {
        ...theme.typography.h3,
        fontWeight: theme.typography.h3.fontWeight?.toString(), // Ensure it's a string
      },
    },
    subtitle: {
      text: '(+43%) than last week',
      align: 'left',
      style: {
        fontSize: theme.typography.subtitle1.fontSize?.toString(), // Convert to string
        fontWeight: theme.typography.subtitle1.fontWeight?.toString(),
        color: theme.typography.subtitle1.color,
        fontFamily: theme.typography.subtitle1.fontFamily,
      },
    },
    xAxis: {
      categories: categories,
      lineColor: theme.palette.divider,
    },
    yAxis: {
      title: {
        text: '',
      },
      gridLineDashStyle: 'Dash',
      gridLineColor: theme.palette.divider,
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {
          return this.value.toString();
        },
      },
    },
    legend: {
      enabled: true,
      align: 'left',
      verticalAlign: 'top',
      // layout: 'horizontal',

      itemStyle: {
        color: theme.typography.subtitle1.color,
        fontSize: theme.typography.subtitle1.fontSize?.toString(),
        fontWeight: theme.typography.subtitle1.fontWeight?.toString(),
        fontFamily: 'Roboto',
      },
      symbolRadius: 50, // Makes it a circle
      symbolHeight: 10, // Controls symbol size
      symbolWidth: 10,
      itemDistance: 30,
      itemMarginBottom: 5,
      labelFormatter: function (): string {
        const values: { [key: string]: string } = {
          'Total Transactions': (
            (typeof summaryTotal === 'number' ? summaryTotal : 0) || 0
          ).toLocaleString('en-US'),
          'Successful Transactions': (
            timeSeries.reduce(
              (acc: number, item: any) => acc + (Number(item.SuccessCount) || 0),
              0
            ) || 0
          ).toLocaleString('en-US'),
          'Failed Transactions': (
            timeSeries.reduce(
              (acc: number, item: any) => acc + (Number(item.FailureCount) || 0),
              0
            ) || 0
          ).toLocaleString('en-US'),
          'Pending Transactions': (
            timeSeries.reduce(
              (acc: number, item: any) => acc + (Number(item.PendingCount) || 0),
              0
            ) || 0
          ).toLocaleString('en-US'),
        };
        return `${(this as any).name}<br/><span style="color:${theme.typography.h1.color}; font-size:${theme.typography.h3.fontSize?.toString()}">${values[(this as any).name] || ''}</span>`;
      },
    },
    plotOptions: {
      area: {
        fillOpacity: 0.1,
        marker: {
          radius: 4,

          lineWidth: 2,
        },
      },
      column: {
        borderRadius: 2,
      },
    },
    series: [
      {
        type: 'area',
        name: 'Total Transactions',
        data:
          timeSeries.map(
            (item: any) =>
              (Number(item.FailureCount) || 0) +
              (Number(item.PendingCount) || 0) +
              (Number(item.SuccessCount) || 0)
          ) || [],
        color: theme.palette.areaChart.totalTransactions,
      },
      {
        type: 'area',
        name: 'Successful Transactions',
        data: timeSeries.map((item: any) => Number(item.SuccessCount) || 0) || [],
        color: theme.palette.areaChart.successTranscations,
        marker: {
          symbol: 'circle',
        },
      },
      {
        type: 'column',
        name: 'Failed Transactions',
        data: timeSeries.map((item: any) => Number(item.FailureCount) || 0) || [],
        color: theme.palette.areaChart.failedTranscations,
      },
      {
        type: 'column',
        name: 'Pending Transactions',
        data: timeSeries.map((item: any) => Number(item.PendingCount) || 0) || [],
        color: (theme.palette as any).areaChart?.pendingTranscations || theme.palette.warning.main,
      },
    ],
  };

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: '10px',
        p: 3,
        flex: 1,
        minWidth: '300px',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          display: 'flex',
          alignItems: 'center',
          zIndex: 1,
        }}
      >
        <Tooltip title="Download Excel">
          <IconButton
            onClick={handleDownload}
            disabled={isDownloading || !data?.length}
            size="small"
            sx={{
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
              '&.Mui-disabled': {
                color: theme.palette.action.disabled,
              },
            }}
            aria-label="Export to Excel"
          >
            {isDownloading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <img src={excelIcon} alt="Export to Excel" style={{ width: 20, height: 20 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Chart */}
      <HighchartsReact highcharts={Highcharts} options={transactionChartOptions} />

      {/* Time period buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
        {periods.map((period) => (
          <TimePeriodButton
            key={period.value}
            label={period.label}
            active={activePeriod === period.value}
            onClick={() => handlePeriodChange(period.value)}
          />
        ))}
      </Box>
    </Box>
  );
};
